import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  ASSISTANT_SUGGESTIONS,
  type AssistantToolCallView,
  type AssistantTurnView,
} from '@fieldops/shared'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { EmailCompose, type ComposeDraft } from '../email/EmailCompose.js'
import { Icon } from '../Icon.js'
import { EcoMark } from '../eco/EcoAvatar.js'
import { EcoAction, EcoChip } from '../eco/EcoButtons.js'
import { Spinner } from '../ui.js'
import { ActionCard } from './assistant/ActionCard.js'
import { Markdown } from './assistant/Markdown.js'
import { sendOnEnter, useConversation } from './assistant/useConversation.js'
import { emailContacts, primaryPerson } from './panes.js'
import { SmsCompose } from './SmsCompose.js'
import type { Job } from './types.js'

/**
 * The job card's Assistant tab: a conversation about this job that can act
 * on it.
 *
 * It resumes this person's latest conversation on the job, or starts one
 * with a "Where this job is" summary. What the assistant does arrives as
 * cards (`assistant/ActionCard.tsx`): reads as steps, notes and people as
 * receipts with Undo, and anything that books, prices or reaches a customer
 * as a card that waits for Approve. SMS still leaves from the phone, through
 * `SmsCompose`, and Edit in composer is `EmailCompose` (rule 40). The words
 * stream over `POST /assistant/run`; everything else is tRPC. Decision 94.
 */
export function AssistantPane({ job }: { job: Job }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const sessions = useQuery(trpc.assistant.sessions.queryOptions({ jobId: job.id }))
  const [sessionId, setSessionId] = useState<string | null>(null)
  const session = useQuery({
    ...trpc.assistant.session.queryOptions({ id: sessionId ?? '' }),
    enabled: sessionId !== null,
  })

  const [text, setText] = useState('')
  const [email, setEmail] = useState<{ call: AssistantToolCallView; draft: ComposeDraft } | null>(null)
  const [sms, setSms] = useState<{ to: string | null; body: string } | null>(null)

  const live = sessions.data?.live ?? false
  const startedFor = useRef<string | null>(null)

  const start = useMutation(trpc.assistant.start.mutationOptions())

  const refreshJob = () => {
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
    void queryClient.invalidateQueries(trpc.inbox.pathFilter())
  }

  const { turns, setTurns, streaming, running, problem, setProblem, scroller, run, approve, reject, undo, undoing } =
    useConversation({
      sessionId,
      saved: session.data?.turns,
      refresh: refreshJob,
      silence: 'The assistant did not answer.',
      onSaved: () => void queryClient.invalidateQueries(trpc.assistant.pathFilter()),
      onPhoneText: ({ to, body }) => setSms({ to, body }),
    })

  const begin = async () => {
    const created = await start.mutateAsync({ jobId: job.id })
    setSessionId(created.id)
    setTurns([])
    void queryClient.invalidateQueries(trpc.assistant.sessions.pathFilter())
    await run({ sessionId: created.id, action: 'summary' })
  }

  // Resume the latest conversation on this job, or open a new one with the
  // summary. Once per job: StrictMode's second effect and a refetch of the
  // list must not start a second conversation.
  useEffect(() => {
    if (!sessions.data || startedFor.current === job.id) return
    startedFor.current = job.id
    const latest = sessions.data.sessions[0]
    if (latest) setSessionId(latest.id)
    else if (sessions.data.live) void begin().catch((error: unknown) => setProblem(errorMessage(error)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions.data, job.id])

  const send = (words: string) => {
    const message = words.trim()
    if (!message || !sessionId || running) return
    setText('')
    void run({ sessionId, action: 'message', text: message })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    send(text)
  }

  const onKeyDown = sendOnEnter(() => send(text))

  const person = primaryPerson(job)
  const firstUser = turns.findIndex((turn) => turn.role === 'USER')
  const canTalk = live && sessionId !== null && !running

  return (
    <section aria-label="Assistant" className="flex min-h-0 flex-1 flex-col bg-ink-50">
      <div
        ref={scroller}
        role="log"
        aria-label="Conversation"
        className="min-h-0 flex-1 overflow-y-auto px-[22px] py-5"
      >
        <div className="mx-auto flex max-w-[880px] flex-col gap-4">
          {sessions.data && !live ? (
            <div className="rounded-lg border border-ink-200 bg-ink-50 px-4 py-3 text-[13px] text-ink-700">
              <p className="font-semibold text-ink-900">The assistant is not connected here.</p>
              <p>It needs a model key on the server (OpenAI or Claude). Everything else on the job card works as usual.</p>
            </div>
          ) : null}

          {turns.map((turn, index) =>
            turn.role === 'USER' ? (
              <UserBubble key={turn.id} text={turn.content} />
            ) : (
              <AssistantTurn
                key={turn.id}
                turn={turn}
                summary={index === 0 && (firstUser === -1 || firstUser > 0) && turn.toolCalls.length === 0}
              >
                {turn.toolCalls.length === 0 ? null : turn.toolCalls.map((call) => (
                  <ActionCard
                    key={call.id}
                    call={call}
                    busy={running}
                    undoing={undoing(call)}
                    onApprove={(edits) => approve(call, edits)}
                    onReject={(reason, resume) => reject(call, reason, resume)}
                    onEditInComposer={
                      call.card?.email ? () => setEmail({ call, draft: call.card!.email! }) : undefined
                    }
                    onUndo={() => undo(call)}
                  />
                ))}
              </AssistantTurn>
            ),
          )}

          {streaming ? <AssistantTurn turn={null} text={streaming} summary={turns.length === 0} /> : null}

          {running && !streaming ? (
            <p className="flex items-center gap-2 pl-10 text-[12.5px] text-ink-500" role="status">
              <Spinner className="size-3.5" /> Working on it…
            </p>
          ) : null}

          {problem ? (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-red-700">
              {problem}
            </p>
          ) : null}
        </div>
      </div>

      <div className="sticky bottom-0 shrink-0 px-[22px] pt-2 pb-4">
        <div className="mx-auto flex max-w-[880px] flex-col gap-2.5">
          <div className="flex items-center gap-1.5">
            <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-0.5" role="group" aria-label="Suggestions">
              {ASSISTANT_SUGGESTIONS.map((suggestion) => (
                <EcoChip key={suggestion.label} disabled={!canTalk} onClick={() => send(suggestion.prompt)}>
                  {suggestion.label}
                </EcoChip>
              ))}
            </div>
            <button
              type="button"
              disabled={!live || running || start.isPending}
              onClick={() => void begin().catch((error: unknown) => setProblem(errorMessage(error)))}
              className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-50"
            >
              <Icon name="plus" className="size-3.5" />
              New chat
            </button>
          </div>

          <form onSubmit={submit} className="ecb-input">
            <textarea
              aria-label="Message the assistant"
              rows={1}
              value={text}
              disabled={!live || sessionId === null}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about this job, or tell the assistant what to write…"
              className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent py-2.5 text-[13.5px] text-ink-900 outline-none field-sizing-content placeholder:text-ink-400"
            />
            <EcoAction badge="send" type="submit" aria-label="Send" title="Send" disabled={!canTalk} />
          </form>
        </div>
      </div>

      <EmailCompose
        open={email !== null}
        onClose={() => setEmail(null)}
        jobId={job.id}
        clientId={job.client.id}
        contacts={emailContacts(job)}
        files={job.attachments.map((file) => ({ id: file.id, name: file.filename }))}
        initial={email?.draft}
        onSent={() => {
          if (email) {
            reject(email.call, 'Edited and sent from the email window instead.', false)
          }
        }}
      />
      <SmsCompose
        open={sms !== null}
        onClose={() => setSms(null)}
        contactName={person?.name ?? job.client.name}
        phone={sms?.to ?? null}
        initialBody={sms?.body}
        jobId={job.id}
      />
    </section>
  )
}

/** Eco's frog beside each answer: every AI control wears it (decision 116). */
function Avatar() {
  return <EcoMark className="size-7" />
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <p className="max-w-[80%] rounded-lg bg-white px-3.5 py-2 text-[13.5px] whitespace-pre-wrap text-ink-900 ring-1 ring-ink-300 ring-inset">
        {text}
      </p>
    </div>
  )
}

function AssistantTurn({
  turn,
  text,
  summary,
  children,
}: {
  turn: AssistantTurnView | null
  text?: string
  summary: boolean
  children?: ReactNode
}) {
  const words = text ?? turn?.content ?? ''
  if (!words.trim() && !children) return null
  return (
    <div className="flex gap-3">
      <Avatar />
      <div className="flex min-w-0 flex-1 flex-col gap-2.5 pt-1">
        {words.trim() ? (
          <Markdown
            text={words}
            lead={summary ? <strong className="font-semibold text-ink-900">Where this job is: </strong> : undefined}
          />
        ) : null}
        {children}
      </div>
    </div>
  )
}
